import React, { useState, useMemo, Fragment, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Typography, Grid, withStyles } from '@material-ui/core';
import { Badge } from '@ds/react-components';
import PageWrapper from 'components/ui/PageWrapper';
import Table from 'components/ui/Table';
import tableMetadata from './tableMetaData';
import SearchFields from './SearchFields';

//fixed date format that conforms to the API, regardless of the region
const dobParameterDateFormat = 'YYYY-MM-DD';
const dobResultDateFormat = 'YYYY-MM-DDTHH:mm:ssZ';

const styles = (theme) => ({
  row: {
    cursor: 'pointer',
  },
  pageHeader: {
    marginBottom: '20px',
  },
  debitsuccessId: {
    color: theme.palette.xpl.text.primary,
    fontWeight: 'bold',
  },
});

const processAccountData = (accounts, dateFormat, classes) => {
  return accounts.map((a) => {
    //If a column needs to be sortable, then it needs to have a rendering field and a sortable(data) field
    const account = { ...a };
    account.id = account.debitsuccessId; //required for table component for selection

    let items = '';
    let sortableStatus = 0;
    if (!account.isActive) {
      items = (
        <Fragment>
          {items}
          <Grid item>
            <Badge variant="error">Cancelled</Badge>
          </Grid>
        </Fragment>
      );
      sortableStatus = 2; //cancelled
    } else if (account.isSuspended) {
      items = (
        <Fragment>
          {items}
          <Grid item>
            <Badge variant="warning">Suspended</Badge>
          </Grid>
        </Fragment>
      );
      sortableStatus = 3; //suspended
    } else {
      items = (
        <Fragment>
          {items}
          <Grid item>
            <Badge variant="success">Active</Badge>
          </Grid>
        </Fragment>
      );
      items = account.isFixedTerm ? (
        <Fragment>
          {items}
          <Grid item>
            <Badge variant="default">Set to Term</Badge>
          </Grid>
        </Fragment>
      ) : (
        <Fragment>
          {items}
          <Grid item>
            <Badge variant="default">Ongoing</Badge>
          </Grid>
        </Fragment>
      );
      sortableStatus = 1; //active + ongoning/fixed
    }
    account.status = sortableStatus;
    account.statusRender = (
      <Grid container spacing={1}>
        {items}
      </Grid>
    );
    account.debitsuccessIdRender = (
      <Typography className={classes.debitsuccessId}>
        {account.debitsuccessId}
      </Typography>
    );

    const dob = moment(account.dob, dobResultDateFormat).add(12, 'hours');
    const minDate = new Date();

    minDate.setFullYear(minDate.getFullYear() - 200);
    account.dobRender = dob.toDate() < minDate ? '' : dob.format(dateFormat);
    account.dob = dob.toDate();
    return account;
  });
};

const CustomerSearch = (props) => {
  const { localisation, searchAccounts, appLoading, classes, accounts } = props;

  const history = useHistory();

  const [formMeta, setFormMeta] = useState({
    searchComplete: false,
    searchClicked: false,
    currentPage: 0,
  });

  const onSearchSubmit = useCallback(
    async (data) => {
      const { search, dob } = data;
      const dobFormatted =
        dob &&
        moment(dob, localisation.locales.dateFormat).format(
          dobParameterDateFormat
        );
      searchAccounts(search, dobFormatted);

      setFormMeta((prevState) => ({
        ...prevState,
        searchComplete: true,
        currentPage: 0,
      }));
    },
    [searchAccounts, setFormMeta, localisation]
  );

  const handleRowClick = (event, id) => {
    history.push(`/Customers/${id}/Overview`);
  };

  const handlePageClick = (data) => {
    setFormMeta((prevState) => ({
      ...prevState,
      currentPage: data.selected,
    }));
  };

  const { dateFormat } = localisation.locales;
  const accountComponents = useMemo(
    () => processAccountData(accounts, dateFormat, classes),
    [accounts, dateFormat, classes]
  );
  const showNoResults =
    accounts.length === 0 && formMeta.searchComplete && !appLoading;
  return (
    <PageWrapper>
      <Typography
        variant="h2"
        color="inherit"
        className={classes.pageHeader}
        noWrap>
        Customers
      </Typography>
      <SearchFields
        onSubmit={onSearchSubmit}
        dateFormat={dateFormat}
        localisation={localisation}
      />

      <Grid container spacing={1} justify="flex-start">
        <Grid item xs={12}>
          <Table
            data={accountComponents}
            metadata={tableMetadata}
            orderBy={'debitsuccessId'}
            onRowClick={handleRowClick}
            classes={{ row: classes.row }}
            showNoResults={showNoResults}
            enableSorting={true}
            showFooter={true}
            noBackground={false}
            showEmptyRows={true}
            currentPage={formMeta.currentPage}
            onPageChange={handlePageClick}
            footerText={
              <div>
                <b>{accountComponents.length}</b> Results Found
              </div>
            }
            showBorder={true}
          />
        </Grid>
      </Grid>
    </PageWrapper>
  );
};

CustomerSearch.propTypes = {
  accounts: PropTypes.arrayOf(PropTypes.object),
  localisation: PropTypes.object.isRequired,
  searchAccounts: PropTypes.func.isRequired,
  appLoading: PropTypes.bool.isRequired,
};

CustomerSearch.defaultProps = {
  accounts: [],
};

export default withStyles(styles)(CustomerSearch);
